import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { ChevronDownIcon } from './Icons';

export interface MultiSelectOption {
  value: string | number;
  label: string;
}

export interface MultiSelectProps {
  options?: Array<string | number | MultiSelectOption>;
  value?: Array<string | number>;
  onChange?: (value: Array<string | number>) => void;
  placeholder?: string;
  className?: string;
  /** Max labels shown in the closed trigger before "+N" */
  maxDisplay?: number;
  /** Disable the control */
  disabled?: boolean;
  /** Show search box above the list (default true) */
  searchable?: boolean;
  /** Optional id prefix for trigger + list (accessibility) */
  id?: string;
}

function normalizeOptions(options: MultiSelectProps['options']): MultiSelectOption[] {
  return (options ?? []).map((opt) => {
    if (typeof opt === 'object' && opt !== null && 'value' in opt) {
      return { value: opt.value, label: opt.label || String(opt.value) };
    }
    return { value: opt as string | number, label: String(opt) };
  });
}

function valueKey(v: string | number): string {
  return typeof v === 'number' ? `n-${v}` : `s-${String(v).replace(/\s/g, '_')}`;
}

function isValueSelected(
  selected: Array<string | number>,
  optionValue: string | number,
): boolean {
  const stringValue = String(optionValue);
  return selected.some(
    (v) => String(v) === stringValue || Number(v) === Number(optionValue),
  );
}

/**
 * Dropdown multi-select: each option is a **native checkbox** + label (keyboard and screen-reader friendly).
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options...',
  className,
  maxDisplay = 2,
  disabled = false,
  searchable = true,
  id: idProp,
}) => {
  const reactId = useId();
  const baseId = idProp ?? `multiselect-${reactId.replace(/:/g, '')}`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(
    null,
  );
  const { colors } = useTheme();

  const updateDropdownRect = useCallback(() => {
    if (!containerRef.current || typeof window === 'undefined') return;
    const rect = containerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const horizontalPad = 8;
    const maxPanel = Math.min(vw - horizontalPad * 2, 36 * 16);
    const width = Math.min(Math.max(rect.width, 200), maxPanel);
    let left = rect.left;
    if (left + width > vw - horizontalPad) {
      left = Math.max(horizontalPad, vw - horizontalPad - width);
    }
    const gap = 4;
    const preferredBelowTop = rect.bottom + gap;
    const spaceBelow = vh - preferredBelowTop - horizontalPad;
    const spaceAbove = rect.top - horizontalPad - gap;
    const desiredMax = 240;
    let top = preferredBelowTop;
    let maxHeight = Math.min(desiredMax, Math.max(120, spaceBelow));
    if (spaceBelow < 120 && spaceAbove > spaceBelow) {
      maxHeight = Math.min(desiredMax, Math.max(120, spaceAbove));
      top = Math.max(horizontalPad, rect.top - gap - maxHeight);
    }
    setDropdownRect({ top, left, width, maxHeight });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inPortal = portalDropdownRef.current?.contains(target);
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownRect(null);
      return;
    }
    updateDropdownRect();
    const onScrollOrResize = () => updateDropdownRect();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updateDropdownRect]);

  const normalizedOptions = normalizeOptions(options);

  const labelMap = new Map<string | number, string>();
  normalizedOptions.forEach((opt) => {
    labelMap.set(opt.value, opt.label);
  });

  const filteredOptions = normalizedOptions.filter((option) => {
    const q = searchTerm.toLowerCase();
    return (
      option.label.toLowerCase().includes(q) || String(option.value).toLowerCase().includes(q)
    );
  });

  const handleToggleOption = useCallback(
    (optionValue: string | number) => {
      if (disabled) return;
      const stringValue = String(optionValue);
      const next = value.some(
        (v) => String(v) === stringValue || Number(v) === Number(optionValue),
      )
        ? value.filter(
            (v) => String(v) !== stringValue && Number(v) !== Number(optionValue),
          )
        : [...value, optionValue];
      onChange?.(next);
    },
    [disabled, onChange, value],
  );

  const handleClearAll = useCallback(() => {
    if (disabled) return;
    onChange?.([]);
  }, [disabled, onChange]);

  const displayText = (): string => {
    if (value.length === 0) return placeholder;
    const labels = value.map((v) => labelMap.get(v) ?? String(v));
    if (value.length <= maxDisplay) return labels.join(', ');
    return `${labels.slice(0, maxDisplay).join(', ')} +${value.length - maxDisplay}`;
  };

  const triggerId = `${baseId}-trigger`;
  const listboxId = `${baseId}-list`;

  const dropdownPanel =
    isOpen && dropdownRect ? (
      <div
        ref={portalDropdownRef}
        id={listboxId}
        role="group"
        aria-labelledby={triggerId}
        className={cn(
          'fixed border rounded-md shadow-lg overflow-hidden flex flex-col',
          colors.surface,
          colors.border,
        )}
        style={{
          top: dropdownRect.top,
          left: dropdownRect.left,
          width: dropdownRect.width,
          maxHeight: dropdownRect.maxHeight,
          ...zStyle(Z_INDEX.modalHigh),
        }}
      >
        {searchable && (
          <div className={cn('p-2 border-b shrink-0', colors.border)}>
            <input
              type="search"
              autoComplete="off"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                'w-full px-2 py-1 text-xs border rounded',
                colors.surface,
                colors.text,
                colors.border,
                'focus:outline-none focus:ring-1 focus:ring-offset-0',
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
              )}
            />
            {value.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className={cn(
                  'mt-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
                  colors.interactiveHover,
                )}
              >
                Clear all ({value.length})
              </button>
            )}
          </div>
        )}

        {!searchable && value.length > 0 && (
          <div className={cn('px-2 py-1 border-b shrink-0', colors.border)}>
            <button
              type="button"
              onClick={handleClearAll}
              className={cn(
                'text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
                colors.interactiveHover,
              )}
            >
              Clear all ({value.length})
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto py-1">
          {filteredOptions.length === 0 ? (
            <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>No options found</div>
          ) : (
            filteredOptions.map((option) => {
              const selected = isValueSelected(value, option.value);
              const optId = `${baseId}-opt-${valueKey(option.value)}`;
              return (
                <label
                  key={optId}
                  htmlFor={optId}
                  className={cn(
                    'flex items-start gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors',
                    colors.interactiveHover,
                    selected ? cn(colors.surfaceHover, colors.primaryFg) : colors.text,
                  )}
                >
                  <input
                    id={optId}
                    type="checkbox"
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600',
                      'focus:ring-indigo-500 focus:ring-offset-0 dark:border-gray-600 dark:bg-gray-800',
                    )}
                    checked={selected}
                    onChange={() => handleToggleOption(option.value)}
                  />
                  <span className="flex-1 leading-tight select-none">{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      </div>
    ) : null;

  return (
    <div className={cn('relative w-full min-w-0', disabled && 'opacity-60 pointer-events-none')} ref={containerRef}>
      <button
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          colors.surface,
          colors.text,
          colors.border,
          colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
          value.length > 0 && cn(colors.primaryBorderAll, colors.surfaceHover),
          className,
        )}
      >
        <span
          className={cn(
            'truncate flex-1 text-left',
            value.length === 0 ? colors.textMuted : colors.text,
          )}
        >
          {displayText()}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <span
              className={cn(
                colors.primary,
                colors.primaryText,
                'text-xs px-1.5 py-0.5 rounded-full font-medium',
              )}
            >
              {value.length}
            </span>
          )}
          <ChevronDownIcon className={cn('w-4 h-4 transition-transform', isOpen ? 'rotate-180' : '')} />
        </div>
      </button>

      {typeof document !== 'undefined' && dropdownPanel ? createPortal(dropdownPanel, document.body) : null}
    </div>
  );
};

export { MultiSelect };
